import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";

export type SidebarTab = "notifications" | "comments" | "tasks";

interface CurrentEntity {
  type: string;
  id: string;
  label?: string;
}

interface RightSidebarContextType {
  isOpen: boolean;
  activeTab: SidebarTab;
  currentEntity: CurrentEntity | null;
  toggleSidebar: () => void;
  openToTab: (tab: SidebarTab) => void;
  closeSidebar: () => void;
  setCurrentEntity: (entity: CurrentEntity | null) => void;
}

const RightSidebarContext = createContext<RightSidebarContextType | undefined>(
  undefined,
);

export function RightSidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTab>("tasks");
  const [currentEntity, setCurrentEntity] = useState<CurrentEntity | null>(
    null,
  );
  const location = useLocation();

  // Clear entity when navigating away
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      setCurrentEntity(null);
    }
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const openToTab = useCallback((tab: SidebarTab) => {
    setActiveTab(tab);
    setIsOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSetCurrentEntity = useCallback((entity: CurrentEntity | null) => {
    setCurrentEntity(entity);
  }, []);

  return (
    <RightSidebarContext.Provider
      value={{
        isOpen,
        activeTab,
        currentEntity,
        toggleSidebar,
        openToTab,
        closeSidebar,
        setCurrentEntity: handleSetCurrentEntity,
      }}
    >
      {children}
    </RightSidebarContext.Provider>
  );
}

export function useRightSidebar() {
  const context = useContext(RightSidebarContext);
  if (context === undefined) {
    throw new Error(
      "useRightSidebar must be used within a RightSidebarProvider",
    );
  }
  return context;
}

export function useSetCurrentEntity(
  type: string,
  id: string | undefined,
  label?: string,
) {
  const { setCurrentEntity } = useRightSidebar();

  useEffect(() => {
    if (id) {
      setCurrentEntity({ type, id, label });
    }
    return () => {
      setCurrentEntity(null);
    };
  }, [type, id, label, setCurrentEntity]);
}
